import { ParsedConfig, ProviderType } from '@/types';

// 增强的 URL 识别模式
const ENHANCED_URL_PATTERNS = [
  // OpenAI 兼容 API
  {
    pattern: /(?:https?:\/\/)?[^\s]*\.openai\.com[^\s]*/i,
    type: ProviderType.OPENAI,
    name: 'OpenAI',
    defaultPath: '/v1',
  },
  {
    pattern: /(?:https?:\/\/)?[^\s]*\.anthropic\.com[^\s]*/i,
    type: ProviderType.ANTHROPIC,
    name: 'Anthropic',
    defaultPath: '/v1',
  },
  {
    pattern: /(?:https?:\/\/)?[^\s]*\.googleapis\.com[^\s]*/i,
    type: ProviderType.GOOGLE_AI,
    name: 'Google AI',
    defaultPath: '/v1',
  },
  {
    pattern: /(?:https?:\/\/)?[^\s]*\.azure\.com[^\s]*/i,
    type: ProviderType.AZURE_OPENAI,
    name: 'Azure OpenAI',
    defaultPath: '',
  },
  // 通用 API 端点
  {
    pattern: /(?:https?:\/\/)?api\.[^\s]+\.[^\s]+/i,
    type: ProviderType.CUSTOM,
    name: 'Custom API',
    defaultPath: '',
  },
  // 任何包含 api 的域名
  {
    pattern: /(?:https?:\/\/)?[^\s]*api[^\s]*\.[^\s]+/i,
    type: ProviderType.CUSTOM,
    name: 'API Endpoint',
    defaultPath: '',
  },
  // 任何有效的 URL
  {
    pattern: /(?:https?:\/\/)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?::\d+)?(?:\/[^\s]*)?/i,
    type: ProviderType.CUSTOM,
    name: 'Generic URL',
    defaultPath: '/v1',
  },
];

// 增强的 API 密钥识别模式
const ENHANCED_KEY_PATTERNS = [
  // 环境变量格式
  {
    pattern: /(?:OPENAI_|ANTHROPIC_|GOOGLE_|AZURE_)?(?:API_)?(?:KEY|SECRET|TOKEN)[\s:=]+['"]?([a-zA-Z0-9_\-\.]{10,})['"]?/i,
    name: 'API Key (env format)',
    captureGroup: 1,
  },
  // 标准 API 密钥格式
  {
    pattern: /sk-[a-zA-Z0-9]{20,}/i,
    name: 'OpenAI-style Key',
  },
  {
    pattern: /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i,
    name: 'UUID Key',
  },
  {
    pattern: /[A-Za-z0-9+/]{20,}={0,2}/,
    name: 'Base64 Key',
  },
  // 通用密钥格式
  {
    pattern: /[a-zA-Z0-9_\-\.]{20,}/,
    name: 'Generic Key',
  },
];

// 环境变量键名映射
const ENV_KEY_MAPPINGS: Record<string, { type: ProviderType; field: 'baseUrl' | 'apiKey' | 'model' }> = {
  'openai_base_url': { type: ProviderType.OPENAI, field: 'baseUrl' },
  'openai_api_key': { type: ProviderType.OPENAI, field: 'apiKey' },
  'openai_model': { type: ProviderType.OPENAI, field: 'model' },
  'anthropic_base_url': { type: ProviderType.ANTHROPIC, field: 'baseUrl' },
  'anthropic_api_key': { type: ProviderType.ANTHROPIC, field: 'apiKey' },
  'anthropic_model': { type: ProviderType.ANTHROPIC, field: 'model' },
  'google_ai_base_url': { type: ProviderType.GOOGLE_AI, field: 'baseUrl' },
  'google_ai_api_key': { type: ProviderType.GOOGLE_AI, field: 'apiKey' },
  'google_ai_model': { type: ProviderType.GOOGLE_AI, field: 'model' },
  'azure_openai_endpoint': { type: ProviderType.AZURE_OPENAI, field: 'baseUrl' },
  'azure_openai_api_key': { type: ProviderType.AZURE_OPENAI, field: 'apiKey' },
  'azure_openai_deployment': { type: ProviderType.AZURE_OPENAI, field: 'model' },
  'base_url': { type: ProviderType.CUSTOM, field: 'baseUrl' },
  'api_key': { type: ProviderType.CUSTOM, field: 'apiKey' },
  'model': { type: ProviderType.CUSTOM, field: 'model' },
  'endpoint': { type: ProviderType.CUSTOM, field: 'baseUrl' },
  'token': { type: ProviderType.CUSTOM, field: 'apiKey' },
};

export class EnhancedConfigParser {
  /**
   * 增强的配置解析
   */
  static parse(text: string): ParsedConfig {
    const result: ParsedConfig = {
      confidence: 0,
      additionalConfig: {},
    };

    // 1. 首先尝试环境变量解析（最高优先级）
    const envResult = this.parseEnvironmentVariables(text);
    if (envResult.baseUrl || envResult.apiKey) {
      Object.assign(result, envResult);
      result.confidence = Math.max(result.confidence, 0.9);
    }

    // 2. 尝试自由文本解析
    const freeTextResult = this.parseFreeText(text);
    if (freeTextResult.baseUrl || freeTextResult.apiKey) {
      // 合并结果，优先使用环境变量结果
      if (!result.baseUrl && freeTextResult.baseUrl) {
        result.baseUrl = freeTextResult.baseUrl;
      }
      if (!result.apiKey && freeTextResult.apiKey) {
        result.apiKey = freeTextResult.apiKey;
      }
      if (!result.model && freeTextResult.model) {
        result.model = freeTextResult.model;
      }
      if (!result.providerType && freeTextResult.providerType) {
        result.providerType = freeTextResult.providerType;
      }
      result.confidence = Math.max(result.confidence, freeTextResult.confidence || 0.7);
    }

    // 3. 清理和标准化 URL
    if (result.baseUrl) {
      result.baseUrl = this.cleanAndCompleteUrl(result.baseUrl, result.providerType);
    }

    // 4. 检测供应商类型（如果未检测到）
    if (result.baseUrl && !result.providerType) {
      result.providerType = this.detectProviderType(result.baseUrl);
    }

    // 5. 计算最终置信度
    if (result.baseUrl && result.apiKey) {
      result.confidence = Math.max(result.confidence, 0.95);
    } else if (result.baseUrl || result.apiKey) {
      result.confidence = Math.max(result.confidence, 0.8);
    }

    return result;
  }

  /**
   * 解析环境变量格式
   */
  private static parseEnvironmentVariables(text: string): ParsedConfig {
    const result: ParsedConfig = {
      confidence: 0.9,
      additionalConfig: {},
    };

    // 环境变量行模式
    const envLinePattern = /^(?:export\s+)?([A-Z_]+)\s*=\s*(['"]?)(.*?)\2$/gm;
    const lines = text.match(envLinePattern) || [];

    for (const line of lines) {
      const match = line.match(/^(?:export\s+)?([A-Z_]+)\s*=\s*(['"]?)(.*?)\2$/);
      if (!match) continue;

      const key = match[1].toLowerCase();
      const value = match[3].trim();

      // 检查键名映射
      for (const [pattern, mapping] of Object.entries(ENV_KEY_MAPPINGS)) {
        if (key.includes(pattern) || pattern.includes(key)) {
          if (mapping.field === 'baseUrl') {
            result.baseUrl = value;
            result.providerType = mapping.type;
          } else if (mapping.field === 'apiKey') {
            result.apiKey = value;
          } else if (mapping.field === 'model') {
            result.model = value;
          }
          break;
        }
      }

      // 通用键名检测
      if (key.includes('url') || key.includes('endpoint') || key.includes('base')) {
        if (!result.baseUrl) {
          result.baseUrl = value;
        }
      } else if (key.includes('key') || key.includes('secret') || key.includes('token')) {
        if (!result.apiKey) {
          result.apiKey = value;
        }
      } else if (key.includes('model')) {
        if (!result.model) {
          result.model = value;
        }
      } else {
        // 存储其他配置
        result.additionalConfig![key] = value;
      }
    }

    return result;
  }

  /**
   * 解析自由文本
   */
  private static parseFreeText(text: string): ParsedConfig {
    const result: ParsedConfig = {
      confidence: 0.7,
      additionalConfig: {},
    };

    // 查找 URL
    for (const pattern of ENHANCED_URL_PATTERNS) {
      const regex = new RegExp(pattern.pattern, 'gi');
      const matches = [...text.matchAll(regex)];
      
      for (const match of matches) {
        const url = match[0];
        if (!result.baseUrl || url.length > result.baseUrl.length) {
          result.baseUrl = url;
          result.providerType = pattern.type;
        }
      }
    }

    // 查找 API 密钥
    for (const pattern of ENHANCED_KEY_PATTERNS) {
      const regex = new RegExp(pattern.pattern, 'gi');
      const matches = [...text.matchAll(regex)];
      
      for (const match of matches) {
        const key = pattern.captureGroup ? match[pattern.captureGroup] : match[0];
        if (key && (!result.apiKey || key.length > result.apiKey.length)) {
          result.apiKey = key;
        }
      }
    }

    // 查找模型名称（简化，只找明显的模型名）
    const modelPatterns = [
      /model[\s:=]+['"]?([a-zA-Z0-9_\-\.]+)['"]?/i,
      /gpt-[\d\.]+(?:-turbo)?(?:-preview)?/i,
      /claude-[\d\.]+/i,
      /gemini-[\w\-]+/i,
      /[\w\-]+-\d+(?:\.\d+)?/i,
    ];

    for (const pattern of modelPatterns) {
      const match = text.match(pattern);
      if (match) {
        const model = match[1] || match[0];
        if (!result.model || model.length > result.model.length) {
          result.model = model;
        }
      }
    }

    return result;
  }

  /**
   * 清理和补全 URL
   */
  static cleanAndCompleteUrl(url: string, providerType?: ProviderType): string {
    if (!url) return url;

    let cleaned = url.trim();

    // 确保有协议头
    if (!cleaned.startsWith('http://') && !cleaned.startsWith('https://')) {
      cleaned = 'https://' + cleaned;
    }

    // 移除末尾的斜杠
    cleaned = cleaned.replace(/\/+$/, '');

    // 根据供应商类型添加默认路径
    if (providerType) {
      const pattern = ENHANCED_URL_PATTERNS.find(p => p.type === providerType);
      if (pattern?.defaultPath && !cleaned.includes(pattern.defaultPath)) {
        // 检查是否已经有版本路径
        const hasVersionPath = /\/v\d+(?:\/|$)/.test(cleaned);
        if (!hasVersionPath) {
          cleaned += pattern.defaultPath;
        }
      }
    }

    // 对于自定义域名，如果没有路径，添加 /v1
    if (providerType === ProviderType.CUSTOM || !providerType) {
      const urlObj = new URL(cleaned);
      if (urlObj.pathname === '/' || !urlObj.pathname) {
        cleaned = cleaned.replace(/\/$/, '') + '/v1';
      }
    }

    return cleaned;
  }

  /**
   * 检测供应商类型
   */
  static detectProviderType(url: string): ProviderType {
    for (const pattern of ENHANCED_URL_PATTERNS) {
      if (pattern.pattern.test(url)) {
        return pattern.type;
      }
    }
    return ProviderType.CUSTOM;
  }

  /**
   * 验证配置
   */
  static validateConfig(config: ParsedConfig): { valid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 验证 URL
    if (!config.baseUrl) {
      errors.push('缺少 API 端点 URL');
    } else {
      try {
        new URL(config.baseUrl);
      } catch {
        errors.push('API 端点 URL 格式无效');
      }
    }

    // 验证 API 密钥
    if (!config.apiKey) {
      errors.push('缺少 API 密钥');
    } else if (config.apiKey.length < 10) {
      warnings.push('API 密钥可能过短');
    }

    // 检查是否需要模型
    if (!config.model) {
      warnings.push('未检测到模型名称，需要手动选择模型');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 获取 API 端点信息
   */
  static getApiEndpointInfo(baseUrl: string, providerType: ProviderType): {
    chatEndpoint: string;
    modelsEndpoint: string;
    healthEndpoint: string;
  } {
    const url = new URL(baseUrl);
    const basePath = url.pathname;

    const endpoints = {
      chatEndpoint: '',
      modelsEndpoint: '',
      healthEndpoint: '',
    };

    switch (providerType) {
      case ProviderType.OPENAI:
        endpoints.chatEndpoint = `${baseUrl}/chat/completions`;
        endpoints.modelsEndpoint = `${baseUrl}/models`;
        endpoints.healthEndpoint = `${baseUrl}/models`;
        break;
      case ProviderType.ANTHROPIC:
        endpoints.chatEndpoint = `${baseUrl}/messages`;
        endpoints.modelsEndpoint = `${baseUrl}/models`;
        endpoints.healthEndpoint = `${baseUrl}/models`;
        break;
      case ProviderType.GOOGLE_AI:
        endpoints.chatEndpoint = `${baseUrl}/models/gemini-pro:generateContent`;
        endpoints.modelsEndpoint = `${baseUrl}/models`;
        endpoints.healthEndpoint = `${baseUrl}/models`;
        break;
      case ProviderType.AZURE_OPENAI:
        endpoints.chatEndpoint = `${baseUrl}/chat/completions`;
        endpoints.modelsEndpoint = `${baseUrl}/models`;
        endpoints.healthEndpoint = `${baseUrl}/models`;
        break;
      default:
        // 自定义 API，尝试常见端点
        endpoints.chatEndpoint = `${baseUrl}/chat/completions`;
        endpoints.modelsEndpoint = `${baseUrl}/models`;
        endpoints.healthEndpoint = `${baseUrl}/health`;
    }

    return endpoints;
  }
}